import React, { useEffect, useMemo, useRef, useState } from 'react';
import { fetchCampaignTemplates } from '../../../lib/api';
import './summary.css';
import './detailsReport.css';

const Summary = () => {
  const iso = (d) => d.toISOString().slice(0, 10);
  const today = useMemo(() => new Date(), []);
  const last30 = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
  }, []);

  const [fromDate, setFromDate] = useState(() => iso(last30));
  const [toDate, setToDate] = useState(() => iso(today));

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const loadInFlightRef = useRef(false);

  const dateParams = () => ({
    from_date: fromDate || undefined,
    to_date: toDate || undefined,
  });

  const load = async (nextPage, { replace = false } = {}) => {
    if (loadInFlightRef.current) return;
    if (!replace && !hasMore) return;
    loadInFlightRef.current = true;
    setLoading(true);
    setError('');
    try {
      const data = await fetchCampaignTemplates({
        page: nextPage,
        limit: 10,
        ...dateParams(),
      });
      if (replace || nextPage === 1) {
        setRows(data);
        setPage(1);
      } else {
        setRows((prev) => [...prev, ...data]);
        setPage(nextPage);
      }
      setHasMore(data.length >= 10);
      if (isInitialLoad) setIsInitialLoad(false);
    } catch (e) {
      setError(e?.message || 'Failed to load');
    } finally {
      loadInFlightRef.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {
    load(1, { replace: true });
  }, []);

  const handleSearch = () => {
    setHasMore(true);
    load(1, { replace: true });
  };

  const handleLoadMore = () => {
    if (loading || !hasMore) return;
    const next = page + 1;
    load(next, { replace: false });
  };

  const fmtDate = (d) => {
    const date = new Date(d);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  const getStatus = (statusCounts, key) => {
    if (!Array.isArray(statusCounts)) return 0;
    const rec = statusCounts.find((s) => s.status === key);
    return rec ? rec.count : 0;
  };

  const downloadCsv = (data, filename = 'campaign_templates.csv') => {
    const csvHeader = [
      'Campaign ID',
      'Campaign Date',
      'WhatsApp Template',
      'Read',
      'Sent',
      'Delivered',
      'Failed',
      'Webhook failed',
      'Pending',
      'Status',
    ];

    const filtered = data.filter((item) => {
      const read = getStatus(item.statusCounts, 'read');
      const sent = getStatus(item.statusCounts, 'sent');
      const delivered = getStatus(item.statusCounts, 'delivered');
      const failed = getStatus(item.statusCounts, 'failed');
      const pending = getStatus(item.statusCounts, 'created');
      const failedCount = item.failedCount || 0;
      return read || sent || delivered || failed || pending || failedCount;
    });

    const escapeCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;

    const csvRows = filtered.map((item) => [
      escapeCsv(item.id),
      escapeCsv(fmtDate(item.created_at)),
      escapeCsv(item.whatsapp_template_name || 'Not Selected'),
      escapeCsv(getStatus(item.statusCounts, 'read')),
      escapeCsv(getStatus(item.statusCounts, 'sent')),
      escapeCsv(getStatus(item.statusCounts, 'delivered')),
      escapeCsv(getStatus(item.statusCounts, 'failed')),
      escapeCsv(getStatus(item.statusCounts, 'created')),
      escapeCsv(item.failedCount || 0),
      escapeCsv(item.status || 'pending'),
    ].join(','));

    const csvContent = [csvHeader.join(','), ...csvRows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const handleExport = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const exportRows = await fetchCampaignTemplates({
        page: 1,
        limit: 1000,
        ...dateParams(),
      });
      downloadCsv(exportRows);
    } catch (e) {
      alert(e?.message || 'Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleRowClick = (row) => {
    const status = (row.status || '').toLowerCase();
    if (status === 'closed') {
      window.location.href = `/main/template-report?campaign_id=${row.id}`;
    } else {
      window.location.href = `/main/send-template?template_id=${row.id}`;
    }
  };

  return (
    <div className="summary-wrap">
      <div className="summary-header">
        <h2>Recent Campaigns</h2>
        <div className="summary-header-actions">
          <div className="summary-meta">{rows.length} loaded</div>
          <button
            type="button"
            className="summary-export-btn"
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>

      <div className="filter-bar filter-bar--compact">
        <div className="field">
          <label htmlFor="summary-from-date">From</label>
          <input
            id="summary-from-date"
            className="filt-control"
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor="summary-to-date">To</label>
          <input
            id="summary-to-date"
            className="filt-control"
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>
        <div className="actions filter-bar-actions">
          <button type="button" className="btn btn-primary" onClick={handleSearch}>
            Search
          </button>
        </div>
      </div>

      {error && <div className="error" style={{ marginBottom: 8 }}>{error}</div>}
      <div className="summary-scroll">
        <div className="summary-table-wrap">
          <table className="summary-table">
            <thead>
              <tr>
                <th>Campaign ID</th>
                <th>Date</th>
                <th>Template</th>
                <th>Total</th>
                <th>Read</th>
                <th>Sent</th>
                <th>Delivered</th>
                <th>Failed</th>
                <th>Pending</th>
                <th>Delivery Failed</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {isInitialLoad && loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={`sk-${i}`} className="skeleton-row">
                    <td><div className="sk sk-text" /></td>
                    <td><div className="sk sk-text" /></td>
                    <td><div className="sk sk-text long" /></td>
                    <td><div className="sk sk-text" /></td>
                    <td><div className="sk sk-text" /></td>
                    <td><div className="sk sk-text" /></td>
                    <td><div className="sk sk-text" /></td>
                    <td><div className="sk sk-text" /></td>
                    <td><div className="sk sk-text" /></td>
                    <td><div className="sk sk-text" /></td>
                    <td><div className="sk sk-pill" /></td>
                  </tr>
                ))
              ) : (
                <>
                  {rows.map((r, idx) => (
                    <tr key={`${r.id}-${idx}`} className="click-row" onClick={() => handleRowClick(r)}>
                      <td data-label="Campaign ID">{r.id}</td>
                      <td data-label="Date">{fmtDate(r.created_at)}</td>
                      <td data-label="Template">{r.whatsapp_template_name || <span className="text-danger">Not Selected</span>}</td>
                      <td data-label="Total">{r.totalnumber}</td>
                      <td data-label="Read">{getStatus(r.statusCounts, 'read')}</td>
                      <td data-label="Sent">{getStatus(r.statusCounts, 'sent')}</td>
                      <td data-label="Delivered">{getStatus(r.statusCounts, 'delivered')}</td>
                      <td data-label="Failed">{getStatus(r.statusCounts, 'failed')}</td>
                      <td data-label="Pending">{getStatus(r.statusCounts, 'created')}</td>
                      <td data-label="Delivery Failed">{r.failedCount}</td>
                      <td data-label="Status">
                        <span className={`badge ${
                          r.status === 'closed' ? 'badge-closed' :
                          r.status === 'created' ? 'badge-created' : 'badge-pending'
                        }`}>
                          {r.status || 'pending'}
                        </span>
                      </td>
                    </tr>
                  ))}
                  {!loading && rows.length === 0 && (
                    <tr><td colSpan={11} className="empty-cell">No campaigns found.</td></tr>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>
        {!isInitialLoad && hasMore && (
          <div className="summary-load-more-wrap">
            <button
              type="button"
              className="summary-load-more-btn"
              onClick={handleLoadMore}
              disabled={loading}
            >
              {loading ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Summary;
